import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CustomPagination from '../../common/components/CustomPagination';
import { DefaultPageSize } from '../../common/utils/commonUtils';
import { getNumberInUSFormat } from '../../common/utils/patientUtils';
import { SortDirs } from '../../common/utils/sortInfo';
import { TimeTrackerResponseItem, TimeTrackerSearchResponse } from '../../types/TimeTracker';
import TimeTrackerGroupTable from './components/TimeTrackerGroupTable';

interface TimeTrackerResultProps {
  timeTrackerData?: TimeTrackerSearchResponse;
  triggerTimeTrackerQuery: (params: any) => Promise<void>;
  editTimeTracker: (timeTracker: TimeTrackerResponseItem) => Promise<void>;
  deleteTimeTracker: (id: number) => void;
}

export default function TimeTrackerResult({
  timeTrackerData,
  triggerTimeTrackerQuery,
  editTimeTracker,
  deleteTimeTracker,
}: TimeTrackerResultProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const [pageErrors, setPageErrors] = useState<string[]>([]);
  const tableRef = useRef<HTMLDivElement>(null);
  const [totalPages, setTotalPages] = useState<number>(0);
  const { timeTrackerGroups, totalTimeTrackerGroup, totalMinutes } = timeTrackerData || {};

  const searchOptions = {
    patientId: searchParams.get('patientId'),
    fromDate: searchParams.get('fromDate'),
    toDate: searchParams.get('toDate'),
    page: searchParams.get('page') || '1',
    pageSize: searchParams.get('pageSize') || DefaultPageSize,
    sort: searchParams.get('sort') || 'trackerMonth',
    dir: searchParams.get('dir') || 'desc',
  };

  const showPagingInfo = totalPages > 1;
  const currentPage = parseInt(searchOptions.page);
  const handlePageChange = (value: any) => {
    const newPage = parseInt(value);
    updateSearchUrl({
      page: newPage,
    });
  };

  useEffect(() => {
    if (totalTimeTrackerGroup) {
      setTotalPages(Math.ceil(Number(totalTimeTrackerGroup) / Number(searchOptions.pageSize)));
    }
  }, [totalTimeTrackerGroup, searchOptions.pageSize]);
  const updateSearchUrl = (updatedData: any) => {
    const params = {
      ...searchOptions,
      ...updatedData,
    };
    if (triggerTimeTrackerQuery) {
      triggerTimeTrackerQuery(params);
    }
  };

  const handleTableSorting = (sortField: string) => {
    if (sortField === searchOptions.sort) {
      const newDir = searchOptions.dir === SortDirs.Asc ? SortDirs.Desc : SortDirs.Asc;
      updateSearchUrl({ dir: newDir });
    } else {
      updateSearchUrl({
        sort: sortField,
        dir: SortDirs.Desc,
      });
    }
  };

  useEffect(() => {
    const hasGroupData = timeTrackerGroups && timeTrackerGroups.length > 0;

    if (!hasGroupData && timeTrackerData) {
      const noDataFoundError = t('timeTracker.error.noDataFound');
      if (pageErrors[0] !== noDataFoundError) {
        setPageErrors([noDataFoundError]);
      }
    } else if (pageErrors.length > 0) {
      setPageErrors([]);
    }
  }, [timeTrackerGroups, timeTrackerData, t, pageErrors]);

  const hasData = timeTrackerGroups && timeTrackerGroups.length > 0;

  return (
    <>
      <Box sx={{ width: '100%' }} ref={tableRef}>
        {hasData && (
          <>
            {timeTrackerGroups && timeTrackerGroups.length > 0 && (
              <TimeTrackerGroupTable
                groups={timeTrackerGroups}
                sort={{ field: searchOptions.sort, dir: searchOptions.dir }}
                onSorted={handleTableSorting}
                editTimeTracker={editTimeTracker}
                deleteTimeTracker={deleteTimeTracker}
              />
            )}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                py: 2,
                paddingBottom: 0,
                pr: matches ? 1 : 20,
                backgroundColor: theme.xPalette.white,
              }}
            >
              <Typography sx={{ fontWeight: 'bold' }}>
                {t('timeTracker.totalMinutes')}: {getNumberInUSFormat(totalMinutes ?? 0)}
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: showPagingInfo ? 4 : 2,
                borderBottomLeftRadius: '15px',
                borderBottomRightRadius: '15px',
                background: theme.xPalette.white,
              }}
            >
              {showPagingInfo && (
                <CustomPagination
                  changePage={(e, value) => handlePageChange(value)}
                  currentPage={currentPage}
                  pages={totalPages}
                  pageRef={tableRef}
                />
              )}
            </Box>
          </>
        )}
        {pageErrors &&
          pageErrors.length > 0 &&
          pageErrors.map((error) => (
            <Alert key={error} severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          ))}
      </Box>
    </>
  );
}
